// contexts/AuthContext.jsx
// Context React pour la gestion de l'authentification utilisateur

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const { t } = useTranslation();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Vérifier si l'utilisateur est déjà connecté (cookie JWT)
  const checkAuth = useCallback(async () => {
    try {
      const response = await fetch('/api/auth/me');
      if (response.ok) {
        const data = await response.json();
        setUser(data.user);
      } else {
        setUser(null);
      }
    } catch (err) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Connexion
  const login = async (email, password) => {
    const response = await fetch('/api/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });

    const data = await response.json();

    if (!response.ok) {
      if (data.code === 'WP_INDISPONIBLE') throw new Error(t('auth.serviceIndisponible'));
      if (data.code === 'EMAIL_NON_CONFIRME') throw Object.assign(new Error(t('auth.emailNonConfirme')), { code: data.code });
      if (data.code === 'TROP_DE_TENTATIVES') throw new Error(t('auth.tropDeTentatives'));
      if (data.code === 'EMAIL_DEJA_UTILISE') throw new Error(t('auth.emailDejaUtilise'));
      if (response.status === 401) throw new Error(t('auth.identifiantsIncorrects'));
      if (response.status === 403) throw new Error(t('auth.compteDesactive'));
      if (response.status === 400) throw new Error(t('auth.champsRequis'));
      throw new Error(t('auth.erreurConnexion'));
    }

    setUser(data.user);
    return data;
  };

  // Inscription
  const register = async (email, password, nom, prenom) => {
    const response = await fetch('/api/auth/register', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password, nom, prenom }),
    });

    const data = await response.json();

    if (!response.ok) {
      if (response.status === 409) throw new Error(t('auth.emailDejaUtilise'));
      if (response.status === 400) throw new Error(t('auth.champsRequis'));
      throw new Error(t('auth.erreurInscription'));
    }

    setUser(data.user);
    return data;
  };

  // Connexion via token WordPress (embed iframe)
  const loginWithWpToken = async (token) => {
    const response = await fetch('/api/auth/wp-token', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ token }),
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Token WP invalide');
    setUser(data.user);
    return data;
  };

  // Mise à jour du profil (nom, prénom, langue...)
  const updateProfil = async (fields) => {
    const response = await fetch('/api/auth/profil', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(fields),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Erreur lors de la mise à jour du profil');
    }

    setUser((prev) => ({ ...prev, ...data.user }));
    return data;
  };

  // Déconnexion
  const logout = async () => {
    try {
      await fetch('/api/auth/logout', { method: 'POST' });
    } catch (err) {
      // On déconnecte quand même côté frontend
    }
    setUser(null);
  };

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    loginWithWpToken,
    updateProfil,
    isAuthenticated: !!user,
    isAdmin: user?.niveauAcces === 'admin',
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth doit être utilisé dans un AuthProvider');
  }
  return context;
}

export default AuthContext;
